import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n/lib';
import { SelectStyled } from '../_helpers/form/Select';
import { TextareaStyledBlock } from '../_helpers/form/Textarea';
import { ErrorPopup } from '../_helpers/form/FormStyles';
import { noHtml } from '../../utilities/validators';

export const PERSONALIZATION_TEXT_MAX_LENGTH = 500;

export interface Personalization {
  color: string;
  text: string;
}

export const withDefaultPersonalizationColor = (
  personalization: Personalization,
  colors: string[],
): Personalization => ({
  ...personalization,
  color: personalization.color || colors[0] || '',
});

export const getPersonalizationError = (
  personalization: Personalization,
): string | undefined =>
  personalization.text.trim()
    ? noHtml(personalization.text)
    : __('Vyplňte text.');

const Wrapper = styled.div`
  margin: ${rem(16)} 0;
`;

const Field = styled.div`
  margin-bottom: ${rem(12)};
`;

const Label = styled.label`
  display: block;
  font-weight: bold;
  margin-bottom: ${rem(4)};
`;

const ColorSelect = styled(SelectStyled)`
  min-width: ${rem(200)};
`;

const TextInput = styled(TextareaStyledBlock)`
  &.error {
    border-color: ${({ theme }) => theme.color.error};
  }
`;

const InfoText = styled.div`
  margin-top: ${rem(8)};
`;

const PersonalizationValues = styled.div`
  white-space: pre-line;
`;

interface ProductPersonalizationFormProps {
  colors: string[];
  infoText?: string;
  personalization: Personalization;
  error?: string;
  onChange: (personalization: Personalization) => void;
  idPrefix?: string;
}

export const ProductPersonalizationForm = ({
  colors,
  infoText,
  personalization,
  error,
  onChange,
  idPrefix = 'personalization',
}: ProductPersonalizationFormProps) => (
  <Wrapper>
    {colors.length > 0 && (
      <Field>
        <Label htmlFor={`${idPrefix}-color`}>{__('Farba atramentu')}</Label>
        <ColorSelect
          id={`${idPrefix}-color`}
          value={personalization.color}
          onChange={(e) =>
            onChange({ ...personalization, color: e.target.value })
          }
        >
          {colors.map((color) => (
            <option key={color} value={color}>
              {color}
            </option>
          ))}
        </ColorSelect>
      </Field>
    )}
    <Field>
      <Label htmlFor={`${idPrefix}-text`}>{__('Text pre pečiatku')}</Label>
      <TextInput
        id={`${idPrefix}-text`}
        rows={4}
        maxLength={PERSONALIZATION_TEXT_MAX_LENGTH}
        value={personalization.text}
        className={error ? 'error' : ''}
        onChange={(e) => onChange({ ...personalization, text: e.target.value })}
      />
      {error && <ErrorPopup>{error}</ErrorPopup>}
    </Field>
    {infoText && <InfoText dangerouslySetInnerHTML={{ __html: infoText }} />}
  </Wrapper>
);

interface PersonalizationInfoProps {
  personalization?: Partial<Personalization> | null;
}

export const PersonalizationInfo = ({
  personalization,
}: PersonalizationInfoProps) =>
  personalization?.text ? (
    <PersonalizationValues>
      {personalization.color && (
        <div>
          <b>{__('Farba')}: </b>
          {personalization.color}
        </div>
      )}
      <div>
        <b>{__('Text')}: </b>
        {personalization.text}
      </div>
    </PersonalizationValues>
  ) : null;
